import useGetRequest from 'hooks/useGetRequest';
import React, { useContext } from 'react';
import { AuthContext } from './AuthContext';
import { Organisation } from 'models/Organisation';
import { StripePayment } from 'models/OrganisationBilling';
import { FileDocument } from 'models/FileDocument';
import { IntegrationConnectionStatus } from 'models/hr-integration/IntegrationConnectionStatus';
import { APIError } from 'lib/_api-helpers';

type OrganisationOverviewContextValue = {
    organisation: Organisation | undefined;
    refetchOrganisation: () => Promise<void>;
    payments: StripePayment[];
    files: FileDocument[];
    refetchFiles: () => Promise<void>;
    connectionStatus: IntegrationConnectionStatus | null;
    loadingConnectionStatus: boolean;
    connectionStatusErrors: APIError[];
    refetchConnectionStatus: () => Promise<void>;
    errors: boolean;
};

export const OrganisationOverviewContext = React.createContext<OrganisationOverviewContextValue>({
    organisation: undefined,
    refetchOrganisation: async () => {},
    payments: [],
    files: [],
    refetchFiles: async () => {},
    connectionStatus: null,
    loadingConnectionStatus: false,
    connectionStatusErrors: [],
    refetchConnectionStatus: async () => {},
    errors: false,
});

export function OrganisationOverviewProvider(props: { children: React.ReactNode }): JSX.Element {
    const { userData } = useContext(AuthContext);
    const organisationId = userData.organisation!.id;

    const {
        data: organisation,
        errors: organisationErrors,
        refetchData: refetchOrganisation,
    } = useGetRequest<Organisation>(`organisations/${organisationId}`);

    const { data: payments, errors: paymentsErrors } = useGetRequest<StripePayment[]>(`billing`);

    const {
        data: files,
        errors: filesErrors,
        refetchData: refetchFiles,
    } = useGetRequest<FileDocument[]>(`organisations/${organisationId}/documents`);

    const {
        data: connectionStatus,
        loading: loadingConnectionStatus,
        errors: connectionStatusErrors,
        refetchData: refetchConnectionStatus,
    } = useGetRequest<IntegrationConnectionStatus>('hr-integration/connection-status');

    let errors: boolean = false;
    if (
        (organisationErrors && organisationErrors.length) ||
        (paymentsErrors && paymentsErrors.length) ||
        (filesErrors && filesErrors.length)
    ) {
        errors = true;
    }

    if (!organisation || !payments || !files) return <></>;

    return (
        <OrganisationOverviewContext.Provider
            value={{
                organisation: organisation,
                refetchOrganisation: refetchOrganisation,
                payments: payments,
                files: files,
                refetchFiles: refetchFiles,
                connectionStatus: connectionStatus,
                loadingConnectionStatus: loadingConnectionStatus,
                connectionStatusErrors: connectionStatusErrors,
                refetchConnectionStatus: refetchConnectionStatus,
                errors: errors,
            }}
        >
            {props.children}
        </OrganisationOverviewContext.Provider>
    );
}
